"use client"

import { useRouter, useSearchParams } from "next/navigation"
import { useExpressCart } from "@/modules/express-checkout/providers/express-cart"
import { useEffect, useMemo } from "react"
import { HttpTypes } from "@medusajs/types"

import ExpressProduct from "../product"
import ExpressAddress from "../address"
import ExpressShipping from "../shipping"
import ExpressPayment from "../payment"

export type ActiveTab = "product" | "address" | "shipping" | "payment"

type RouterProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  countryCode: string
}

/**
 * Router component manages the express checkout flow navigation
 * It validates cart state before allowing step progression
 */
const ExpressRouter = ({ product, region, countryCode }: RouterProps) => {
  const { cart } = useExpressCart()
  const searchParams = useSearchParams()
  const router = useRouter()
  const currentStep = searchParams.get("step")

  // Check if the cart has the correct product
  const isCartValid = useMemo(() => {
    if (!cart?.items?.length) return false
    return cart.items.some(
      (item) => (item.product_id || item.product?.id) === product.id
    )
  }, [cart, product.id])

  // Determine active tab based on step param and cart state
  const activeTab: ActiveTab = useMemo(() => {
    if (
      currentStep === "product" ||
      currentStep === "address" ||
      currentStep === "shipping" ||
      currentStep === "payment"
    ) {
      return currentStep
    }
    return "product"
  }, [currentStep])

  // Validate step transitions
  useEffect(() => {
    if (!cart) return

    const basePath = `/${countryCode}/products/${product.handle}`

    // If not on product step and cart is empty or invalid, redirect to product
    if (activeTab !== "product" && !isCartValid) {
      router.push(`${basePath}?express=true`)
      return
    }

    // If on shipping step but no address, redirect to address
    if (
      activeTab === "shipping" &&
      (!cart?.shipping_address || !cart?.billing_address)
    ) {
      router.push(`${basePath}?express=true&step=address`)
      return
    }

    // If on payment step but no shipping method or address, redirect appropriately
    if (activeTab === "payment") {
      if (!cart?.shipping_address || !cart?.billing_address) {
        router.push(`${basePath}?express=true&step=address`)
        return
      }
      if (!cart?.shipping_methods?.length) {
        router.push(`${basePath}?express=true&step=shipping`)
        return
      }
    }
  }, [isCartValid, activeTab, cart, countryCode, product.handle, router])

  return (
    <div className="flex flex-col gap-4 w-full max-w-2xl mx-auto">
      <ExpressProduct
        product={product}
        region={region}
        countryCode={countryCode}
        isActive={activeTab === "product"}
        isCompleted={isCartValid && activeTab !== "product"}
        isCartValid={isCartValid}
      />
      <ExpressAddress
        countryCode={countryCode}
        productHandle={product.handle!}
        isActive={activeTab === "address"}
        isCompleted={
          !!cart?.shipping_address &&
          !!cart?.billing_address &&
          activeTab !== "address"
        }
      />
      <ExpressShipping
        countryCode={countryCode}
        productHandle={product.handle!}
        isActive={activeTab === "shipping"}
        isCompleted={!!cart?.shipping_methods?.length && activeTab !== "shipping"}
      />
      <ExpressPayment
        countryCode={countryCode}
        productHandle={product.handle!}
        isActive={activeTab === "payment"}
      />
    </div>
  )
}

export default ExpressRouter












